// src/services/api.js
import axios from "axios";
import { useAuth } from "../composables/useAuth";
import { useMagicLink } from "../composables/useMagicLink";

const { getToken, logout } = useAuth();

// Create an axios instance with default config
const getBaseURL = () => {
  return "/";
};

const apiClient = axios.create({
  baseURL: getBaseURL(),
  headers: {
    "Content-Type": "application/json",
  },
  validateStatus: (status) => status >= 200 && status < 300,
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.log("Unauthorized - logging out");
      logout();
      return Promise.reject(new Error("Unauthorized"));
    }
    // Only network/timeout/CORS/etc end up here
    return Promise.reject(error);
  }
);

// EOR magic-link POC (ClickUp 86ba8whkq). A SEPARATE axios instance so its
// interceptor attaches the EOR session token (not the staff JWT) and a 401 here
// does NOT trigger the staff logout flow above.
const eorClient = axios.create({
  baseURL: getBaseURL(),
  headers: { "Content-Type": "application/json" },
  validateStatus: (status) => status >= 200 && status < 300,
});

eorClient.interceptors.request.use((config) => {
  const token = useMagicLink().getSession();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const eorAPI = {
  // Ask the backend to email a magic link to an allowlisted address.
  requestLink: (email) => {
    return eorClient.post("/api/eor/request", { email });
  },
  // Exchange a single-use link token for a session token + the EOR id.
  redeem: (token) => {
    return eorClient.post("/api/eor/redeem", { token });
  },
  // Fetch the EOR document using the stored session token.
  getEOR: (id) => {
    return eorClient.get(`/api/eor/${id}`);
  },
};

export const transactionBatchAPI = {
  getTransactionDetail: (id) => {
    return apiClient.get(`/api/transaction-batch/${id}/detail`);
  },
  downloadTransactionBatchDetail: (id) => {
    return apiClient.get(`/api/transaction-batch/${id}/detail/download`, {
      responseType: "blob",
    });
  },
};

// Auth-related API calls
export const authAPI = {
  login: (credentials) => {
    return apiClient.post("/api/login", credentials);
  },
  // Source of truth for the current user — called on page reload to
  // rehydrate role state. Login response already includes this info, so
  // we only need /me when the in-memory user is empty.
  me: () => {
    return apiClient.get("/api/v1/me");
  },
  // Self-service password change for the current user.
  changePassword: (payload) => {
    return apiClient.put("/api/v1/user/password", payload);
  },
};

// Admin-only API. Every endpoint requires a role on the backend
// (api_client_admin for /api-clients/*, role_admin for /users/*).
// The UI also gates menu/tab visibility — server is the real enforcer.
export const adminAPI = {
  // API client management.
  listApiClients: () => {
    return apiClient.get("/api/v1/admin/api-clients");
  },
  createApiClient: (body) => {
    // body = {tenant, description}
    return apiClient.post("/api/v1/admin/api-clients", body);
  },
  deactivateApiClient: (id) => {
    return apiClient.post(
      `/api/v1/admin/api-clients/${encodeURIComponent(id)}/deactivate`
    );
  },
  rotateApiClient: (id) => {
    return apiClient.post(
      `/api/v1/admin/api-clients/${encodeURIComponent(id)}/rotate`
    );
  },

  // Role / user management.
  listUsers: () => {
    return apiClient.get("/api/v1/admin/users");
  },
  getUserRoles: (username) => {
    return apiClient.get(
      `/api/v1/admin/users/${encodeURIComponent(username)}/roles`
    );
  },
  grantRole: (username, role) => {
    return apiClient.post(
      `/api/v1/admin/users/${encodeURIComponent(username)}/roles`,
      { role }
    );
  },
  revokeRole: (username, role) => {
    return apiClient.delete(
      `/api/v1/admin/users/${encodeURIComponent(username)}/roles/${encodeURIComponent(role)}`
    );
  },
};

// Dashboard-related API calls
export const dashboardAPI = {
  listEmployersByCustomerId: (customerId) => {
    return apiClient.get(`/api/v1/customers/${customerId}/employers`);
  },
  addEmployer: (customerId, employerCode) => {
    return apiClient.put(
      `/api/v1/customers/${customerId}/employers/${employerCode}`
    );
  },
  deleteEmployer: (customerId, employerCode) => {
    return apiClient.delete(
      `/api/v1/customers/${customerId}/employers/${employerCode}`
    );
  },
  getCustomers: () => {
    return apiClient.get("/api/v1/customers");
  },
  getVendors: (params = {}) => {
    return apiClient.get("/api/v1/vendors", { params });
  },
  getVendorById: (vendorId) => {
    return apiClient.get(`/api/v1/vendors/${vendorId}`);
  },
  patchVendor: (vendorId, payload) => {
    return apiClient.patch(`/api/v1/vendors/${vendorId}`, payload);
  },
  deleteVendor: (vendorId) => {
    return apiClient.delete(`/api/v1/vendors/${vendorId}`);
  },
  getUnkownVendors: () => {
    return apiClient.get("/api/v1/vendors/unknown");
  },
  approveVendor: (vendorId, payload) => {
    return apiClient.post(`/api/v1/vendors/${vendorId}/approve`, payload);
  },
  rejectVendor: (vendorId, payload) => {
    return apiClient.post(`/api/v1/vendors/${vendorId}/reject`, payload);
  },
  getVendorSourceMappings: (params = {}) => {
    return apiClient.get("/api/v1/vendor-source-mappings", { params });
  },
  resolveVendorSourceMapping: (mappingId, payload) => {
    return apiClient.post(`/api/v1/vendor-source-mappings/${mappingId}/resolve`, payload);
  },
  listApprovedEmails: (vendorId) => {
    return apiClient.get(`/api/v1/vendors/${vendorId}/eor-approved-emails`);
  },
  addApprovedEmail: (vendorId, email) => {
    return apiClient.post(`/api/v1/vendors/${vendorId}/eor-approved-emails`, { email });
  },
  removeApprovedEmail: (vendorId, emailId) => {
    return apiClient.delete(`/api/v1/vendors/${vendorId}/eor-approved-emails/${emailId}`);
  },
  // Bulk-approve EOR viewer emails from a tax_id,email CSV. dryRun returns
  // the same report without writing anything.
  importApprovedEmails: (file, dryRun) => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post("/api/v1/vendors/eor-approved-emails/import", formData, {
      params: { dryRun },
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getBatches: (params = {}) => {
    return apiClient.get("/api/v1/batches", { params });
  },
  getBankPaymentFilesByBatchId: (batchId) => {
    return apiClient.get(`/api/v1/batch/${batchId}/bank-payment-files`);
  },
  getTransactionBatches: () => {
    return apiClient.get(`/api/v1/transaction-batches`);
  },
  getTransactionBatchById: (id) => {
    return apiClient.get(`/api/v1/transaction-batches/${id}`);
  },
  getTransactionBatchesByBankPaymentFileId: (bankPaymentFileId) => {
    return apiClient.get(
      `/api/v1/bank-payment-file/${bankPaymentFileId}/transaction-batches`
    );
  },
  getTransactions: () => {
    return apiClient.get(`/api/v1/transactions`);
  },
  getTransactionsByBatchId: (batchId) => {
    return apiClient.get(`/api/v1/transaction-batch/${batchId}/transactions`);
  },
  getTransactionsPaginated: (params = {}) => {
    return apiClient.get('/api/v1/transactions', { params });
  },
  getTransactionsSum: (params = {}) => {
    return apiClient.get('/api/v1/transactions/sum', { params });
  },
  downloadTransactionsCSV: (params = {}) => {
    // Add format=csv to params
    const csvParams = { ...params, format: 'csv' };
    return apiClient.get('/api/v1/transactions', {
      params: csvParams,
      responseType: 'blob'
    });
  },
  regenerateTransactionBatchIds: (batchId) => {
    return apiClient.post(`/api/v1/transaction-batch/${batchId}/regenerate-ids`);
  },
  updateBatch: (batchId, field, value) => {
    return apiClient.patch(`/api/v1/batch/${batchId}/${field}/${value}`);
  },
  deleteBatch: (batchId) => {
    return apiClient.delete(`/api/v1/batch/${batchId}`);
  },
  updateTransactionBatchStatus: (transactionBatchId, status) => {
    return apiClient.patch(
      `/api/v1/transaction-batch/${transactionBatchId}/status/${status}`
    );
  },
  updateBankPaymentFile: (bankPaymentFileId, field, value) => {
    return apiClient.patch(
      `/api/v1/bank-payment-file/${bankPaymentFileId}/${field}/${value}`
    );
  },
  uploadPaymentFile: async (customerId, registerDate, file, employerCode = null) => {
    const formData = new FormData();
    formData.append("paymentFile", file);
    if (employerCode != null && employerCode !== "") {
      formData.append("employerCode", employerCode);
    }

    const token = getToken();

    const formattedDate = registerDate.toISOString().split("T")[0];

    const response = await fetch(
      `/api/v1/customer/${customerId}/${formattedDate}/payment-file`,
      {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token.trim()}` : "",
        },
        cache: "no-cache",
        body: formData,
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        console.log("Unauthorized - logging out");
        logout();
        throw new Error("Unauthorized");
      }

      // Handle 422 Unprocessable Entity (validation errors)
      if (response.status === 422) {
          const errorData = await response.json();
          // Create a generic validation error object
          const error = new Error(errorData.error || "Validation failed");
          error.isValidationError = true;
          error.errorType = errorData.error; // "Failed to resolve vendors" or "Invalid payment types"
          error.details = errorData.details; // The full details object
          throw error;
      }

      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorBody = await response.text();
        if (errorBody) {
          errorMessage = errorBody;
        }
      } catch (e) {
        console.error("Failed to read error response body:", e);
      }
      throw new Error(errorMessage);
    }

    return true;
  },
  downloadPaymentFile: (bankPaymentFileId, preview) => {
    let url = `/api/v1/bank-payment-file/${bankPaymentFileId}`;
    if (preview) {
      url += "?mode=json";
    }
    return apiClient.get(url, {
      responseType: preview ? "json" : "blob",
    });
  },
  pushBankPaymentFile: (bankPaymentFileId) => {
    return apiClient.post(`/api/v1/bank-payment-file/${bankPaymentFileId}/push`);
  },
  downloadConfirmationFile: (customerId, full = false) => {
    const url =
      full === true
        ? `/api/v1/confirmation-file/${customerId}?full=true`
        : `/api/v1/confirmation-file/${customerId}`;
    return apiClient.get(url, {
      responseType: "blob",
    });
  },
  downloadInvoiceExport: (customerId, startDate, endDate) => {
    const start = startDate.toISOString().split("T")[0]; // YYYY-MM-DD
    const end = endDate.toISOString().split("T")[0]; // YYYY-MM-DD
    return apiClient.get(`/api/v1/customers/${customerId}/invoice-export`, {
      params: { start_date: start, end_date: end },
      responseType: "blob",
    });
  },
  exportPaymentAcknowledgement: (customerId, includeExported = false) => {
    const params = includeExported ? { includeExported: "true" } : {};
    return apiClient.get(
      `/api/v1/customers/${customerId}/payment-acknowledgement`,
      { params, responseType: "blob" }
    );
  },
  uploadVendorFile: (file) => {
    const formData = new FormData();
    formData.append("vendorFile", file);

    const token = getToken();

    console.log(token);

    return fetch("/api/v1/vendor-file", {
      method: "POST",
      headers: {
        Authorization: token ? `Bearer ${token.trim()}` : "",
        // Note: Don't set Content-Type for multipart/form-data with fetch
        // It will be set automatically with the correct boundary
      },
      cache: "no-cache",
      body: formData,
    }).then((response) => {
      if (response.status === 401) {
        console.log("Unauthorized - logging out");
        logout();
        throw new Error("Unauthorized");
      }
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return true;
    });
  },
  uploadReconciliationFile: (reconType, file) => {
    const formData = new FormData();
    formData.append("reconciliationFile", file);

    const token = getToken();

    console.log(token);

    return fetch(`/api/v1/reconciliation-file/${reconType}`, {
      method: "POST",
      headers: {
        Authorization: token ? `Bearer ${token.trim()}` : "",
        // Note: Don't set Content-Type for multipart/form-data with fetch
        // It will be set automatically with the correct boundary
      },
      cache: "no-cache",
      body: formData,
    }).then((response) => {
      if (response.status === 401) {
        console.log("Unauthorized - logging out");
        logout();
        throw new Error("Unauthorized");
      }
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return true;
    });
  },
  resolveVendorIds: () => {
    return apiClient.post("/api/v1/admin/resolve-vendor-ids", { confirm: true });
  },
};

export const sftpAPI = {
  listClients: () => apiClient.get("/api/v1/sftp-clients"),
  getClient: (id) => apiClient.get(`/api/v1/sftp-clients/${id}`),
  createClient: (body) => apiClient.post("/api/v1/sftp-clients", body),
  updateClient: (id, body) => apiClient.put(`/api/v1/sftp-clients/${id}`, body),
  triggerPull: (id) => apiClient.post(`/api/v1/sftp-clients/${id}/trigger-pull`),
  getPullJob: (jobId) => apiClient.get(`/api/v1/sftp-pull-jobs/${jobId}`),
  listPullJobs: (id) => apiClient.get(`/api/v1/sftp-clients/${id}/pull-jobs`),
};
